import { useEffect, useRef, useState, type ComponentType, type PointerEvent } from "react";
import {
  BookOpen,
  Camera,
  LayoutDashboard,
  Github,
  Home,
  Link as LinkIcon,
  Linkedin,
  Mail,
  UserRound,
  type LucideProps
} from "lucide-react";
import { setPointerGlow } from "./pointer";
import "./styles.css";

type Theme = "light" | "dark";

export type SiteNavItem = {
  label: string;
  href: string;
  icon?: ComponentType<LucideProps>;
};

export type SocialLink = {
  label: string;
  href: string;
  icon: ComponentType<LucideProps>;
};

type SiteNavProps = {
  brand: {
    label: string;
    href: string;
    ariaLabel: string;
  };
  items: readonly SiteNavItem[];
  activeLabel: string;
  socialLinks?: readonly SocialLink[];
};

export const defaultSocialLinks: SocialLink[] = [
  {
    label: "GitHub",
    href: "https://github.com/AlbertHuangKSFO",
    icon: Github
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/jinze-huang/",
    icon: Linkedin
  },
  {
    label: "Email",
    href: "mailto:hi@jinze.ai",
    icon: Mail
  }
];

type PublicNavOptions = {
  underConstructionHref?: (app: "blog" | "photo" | "links") => string;
};

export function createPublicNavItems({
  underConstructionHref = (app) =>
    app === "blog"
      ? "https://blog.jinze.ai"
      : `https://jinze.ai/under-construction?app=${encodeURIComponent(app)}`
}: PublicNavOptions = {}): SiteNavItem[] {
  return [
    { label: "Home", href: "https://jinze.ai", icon: Home },
    { label: "Me", href: "https://me.jinze.ai", icon: UserRound },
    { label: "Blog", href: underConstructionHref("blog"), icon: BookOpen },
    { label: "Photo", href: underConstructionHref("photo"), icon: Camera },
    { label: "Dash", href: "https://dash.jinze.ai", icon: LayoutDashboard },
    { label: "Links", href: underConstructionHref("links"), icon: LinkIcon }
  ];
}

function getInitialTheme(): Theme {
  const storedTheme = window.localStorage.getItem("jinze-theme");

  if (storedTheme === "light" || storedTheme === "dark") {
    return storedTheme;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function SiteNav({
  activeLabel,
  brand,
  items,
  socialLinks = defaultSocialLinks
}: SiteNavProps) {
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme());
  const navRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem("jinze-theme", theme);
  }, [theme]);

  function handleNavPointerMove(event: PointerEvent<HTMLElement>) {
    const nav = navRef.current;
    if (!nav) return;

    const rect = nav.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    nav.style.setProperty("--nav-x", `${x}%`);
  }

  function toggleTheme() {
    setTheme((currentTheme) => (currentTheme === "light" ? "dark" : "light"));
  }

  return (
    <nav
      ref={navRef}
      className="site-nav"
      aria-label="Primary navigation"
      onPointerMove={handleNavPointerMove}
    >
      <a className="brand" href={brand.href} aria-label={brand.ariaLabel}>
        {brand.label}
      </a>
      <div className="nav-links">
        {items.map(({ href, icon: Icon, label }) => (
          <a
            key={href}
            href={href}
            aria-current={label === activeLabel ? "page" : undefined}
            onPointerMove={setPointerGlow}
          >
            {Icon ? <Icon aria-hidden="true" size={15} strokeWidth={2} /> : null}
            <span>{label}</span>
          </a>
        ))}
      </div>
      <div className="nav-actions">
        <div className="social-links" aria-label="Social links">
          {socialLinks.map(({ href, icon: Icon, label }) => (
            <a key={label} href={href} aria-label={label} onPointerMove={setPointerGlow}>
              <Icon aria-hidden="true" size={17} strokeWidth={2} />
            </a>
          ))}
        </div>
        <button
          className="theme-toggle"
          type="button"
          onClick={toggleTheme}
          onPointerMove={setPointerGlow}
          aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
        >
          {theme === "light" ? "Dark" : "Light"}
        </button>
      </div>
    </nav>
  );
}
